import { call, put, select, takeEvery } from 'redux-saga/effects';
import axiosInstance from 'utils/axiosUtil';
import {
  TRENDING_DATA_LOAD,
  TRENDING_DATA_LOADING,
  TRENDING_DATA_SUCCESS,
  TRENDING_DATA_ERROR,
  TRENDING_ACCESS_SUCCESS,
  TRENDING_AFFILIATED_LOAD,
  TRENDING_AFFILIATED_LOADING,
  TRENDING_AFFILIATED_SUCCESS,
  TRENDING_AFFILIATED_ERROR,
  TrendingDataType,
  trendingKey,
} from 'store/actions/trendingActions';

// Same domain for every environment: the bucket is shared by staging and
// production and keyed only by cross-environment identifiers.
const DATA_BASE_URL = 'https://data.doctivityhealth.com/v1';

// dataType -> file inside the entity's <year>/Q<q>/ directory
// (see doctivity-rest/docs/data/historical-data.md for the tree layout)
const FILE_BY_TYPE = {
  [TrendingDataType.PROJECTED_PATIENTS]: 'patients.jsonl.gz',
  [TrendingDataType.PEDIATRIC_PROJECTED_PATIENTS]: 'peds/patients.jsonl.gz',
  [TrendingDataType.CAPTURED_CLAIMS]: 'codes-dx.jsonl.gz',
  [TrendingDataType.PATIENT_MIGRATION]: 'migrations-county.jsonl.gz',
  [TrendingDataType.SHARED_AFTER]: 'shared-after.jsonl.gz',
  [TrendingDataType.SHARED_BEFORE]: 'shared-before.jsonl.gz',
};

// refresh the signed cookie this long before its stated expiry
const ACCESS_SLACK_MS = 5 * 60 * 1000;

// Fetches the CloudFront signed cookies from doctivity-rest. The cookies land
// on .doctivityhealth.com (HttpOnly), so all we track in the store is the
// expiry time the endpoint reports.
//
// DEV: the request must be SAME-ORIGIN through the webpack dev-server proxy
// (/api -> localhost API). A direct call to http://localhost:3000 gets a
// response whose Set-Cookie (Domain=.doctivityhealth.com) the browser
// discards — localhost can't set cookies for that domain — and every data
// fetch then 403s. Same-origin on https://dev.doctivityhealth.com:<port>,
// the cookies land. (See historical-data-cloudfront.md, Local development.)
//
// PROD: api.doctivityhealth.com shares the parent domain, so a normal
// credentialed CORS request can set the cookies; withCredentials is required
// for the browser to store them.
async function fetchDataAccess() {
  if (process.env.NODE_ENV !== 'production') {
    const response = await fetch('/api/v2/data/access', {
      credentials: 'include',
      headers: {
        authorization: `Bearer ${localStorage.getItem('auth') || 'anonymous'}`,
      },
    });
    if (!response.ok) {
      throw new Error(`data access endpoint returned ${response.status}`);
    }
    return await response.json(); // { dataUrl, expiresAt }
  }
  const response = await axiosInstance.get('/v2/data/access', {
    withCredentials: true,
  });
  return response.data; // { dataUrl, expiresAt }
}

// Simple GET (no custom headers -> no CORS preflight; an OPTIONS request
// would carry no cookies and be rejected by the signed-cookie check).
// 404 means the entity has no file for that quarter — normal, not an error.
async function fetchNdjson(url) {
  const response = await fetch(url, { credentials: 'include' });
  if (response.status === 404 || response.status === 403) {
    return { status: response.status, rows: null };
  }
  if (!response.ok) {
    throw new Error(`${response.status} loading ${url}`);
  }
  const text = await response.text();
  const rows = [];
  for (const line of text.split('\n')) {
    if (line) rows.push(JSON.parse(line));
  }
  return { status: response.status, rows };
}

// All quarter loads for a chart start in the same tick, before the first
// access response can land in the store — sharing one in-flight promise
// coalesces them onto a single /v2/data/access call instead of one per file.
let accessInFlight = null;
function fetchDataAccessShared() {
  if (!accessInFlight) {
    accessInFlight = fetchDataAccess().finally(() => {
      accessInFlight = null;
    });
  }
  return accessInFlight;
}

function* ensureDataAccess(force = false) {
  const expiresAt = yield select((state) => state.trending.accessExpiresAt);
  if (
    !force &&
    expiresAt &&
    new Date(expiresAt).getTime() - ACCESS_SLACK_MS > Date.now()
  ) {
    return;
  }
  const access = yield call(fetchDataAccessShared);
  yield put({ type: TRENDING_ACCESS_SUCCESS, expiresAt: access.expiresAt });
}

// keys currently being fetched (in-flight dedupe across same-tick dispatches)
const inFlight = new Set();

function* loadTrendingData(action) {
  const { entityType, id, year, quarter, dataType } = action;
  const key = trendingKey(entityType, id, year, quarter, dataType);

  const existing = yield select((state) => state.trending.entries[key]);
  if (inFlight.has(key) || (existing && (existing.loading || existing.loaded))) {
    return; // already fetched or being fetched; browser cache covers evictions
  }
  const file = FILE_BY_TYPE[dataType];
  if (!file || !id) {
    yield put({ type: TRENDING_DATA_ERROR, key, error: 'bad request' });
    return;
  }

  inFlight.add(key);
  yield put({ type: TRENDING_DATA_LOADING, key });
  try {
    yield call(ensureDataAccess);
    const url = `${DATA_BASE_URL}/${entityType}/${id}/${year}/Q${quarter}/${file}`;
    // A 403 is ambiguous here: it can mean a missing/expired cookie, OR a
    // MISSING OBJECT — with Origin Access Control, S3 answers nonexistent
    // keys with 403 AccessDenied unless the bucket policy also grants
    // CloudFront s3:ListBucket. And CloudFront-generated 403s may carry no
    // CORS headers, surfacing as an opaque TypeError. So: refresh the cookie
    // and retry once; if it STILL fails 403/opaque, the cookie is proven
    // fresh and the object simply isn't there — record the quarter as
    // missing (red X), not as an error (amber).
    let result;
    try {
      result = yield call(fetchNdjson, url);
    } catch (err) {
      result = { status: 403, rows: null };
    }
    if (result.status === 403) {
      yield call(ensureDataAccess, true);
      try {
        result = yield call(fetchNdjson, url);
      } catch (err) {
        result = { status: 403, rows: null };
      }
      if (result.status === 403) {
        result = { status: 404, rows: null }; // missing object behind OAC
      }
    }
    yield put({
      type: TRENDING_DATA_SUCCESS,
      key,
      // 404 -> entity has no data that quarter: cache an empty result
      data: result.rows || [],
    });
  } catch (error) {
    console.error('trending load failed:', key, error);
    yield put({ type: TRENDING_DATA_ERROR, key, error: String(error && error.message) });
  } finally {
    inFlight.delete(key);
  }
}

// One query for every affiliated provider NPI of a client, cached per client
// in the reducer. Uses the same related_clients/through pattern the provider
// load action uses; only id+npi come back, so even thousands of affiliated
// providers are a small payload.
async function fetchAffiliatedNpis(clientId) {
  const response = await axiosInstance.get('/Providers', {
    params: {
      opts: {
        attributes: ['id', 'npi'],
        include: [
          {
            association: 'related_clients',
            attributes: ['id'],
            required: true,
            where: { id: clientId },
            through: { as: 'options', attributes: [], where: { is_affiliated: true } },
            duplicating: false,
          },
        ],
        limit: 50000,
      },
    },
  });
  const rows = response.data?.rows || response.data || [];
  return rows.map((r) => r.npi).filter(Boolean);
}

function* loadAffiliated(action) {
  const { clientId } = action;
  if (!clientId) return;
  const existing = yield select(
    (state) => state.trending.affiliatedByClient[clientId],
  );
  if (existing && (existing.loading || existing.loaded)) return;
  yield put({ type: TRENDING_AFFILIATED_LOADING, clientId });
  try {
    const npis = yield call(fetchAffiliatedNpis, clientId);
    yield put({ type: TRENDING_AFFILIATED_SUCCESS, clientId, npis });
  } catch (error) {
    console.error('affiliated npis load failed:', clientId, error);
    yield put({
      type: TRENDING_AFFILIATED_ERROR,
      clientId,
      error: String(error && error.message),
    });
  }
}

export function* watchTrendingData() {
  yield takeEvery(TRENDING_DATA_LOAD, loadTrendingData);
  yield takeEvery(TRENDING_AFFILIATED_LOAD, loadAffiliated);
}
